import { useQuery } from '@tanstack/react-query';
import type { DataLayer, ReadingsResponse } from '@atnt/shared';
import { api } from '@/shared/api/client';
import { useLiveTenantId, useSensorHistory } from './LiveProvider';
import type { HistoryPoint } from './live-store';

type Range = DataLayer['defaultRange'];

/**
 * History for charts and tables: the stored series for `range` (GET /tenants/:t/readings, one bucket grid for all
 * ids) followed by the values received live after the last stored point, so the chart keeps moving between
 * refetches. Must be used inside `<LiveProvider>`.
 */
export function useSensorHistoryRange(
  sensorIds: readonly (string | null | undefined)[],
  range: Range,
): { history: ReadonlyMap<string, readonly HistoryPoint[]>; isLoading: boolean } {
  const tenantId = useLiveTenantId();
  const ids = [...new Set(sensorIds.filter((x): x is string => Boolean(x)))].sort();
  const live = useSensorHistory(ids);
  const query = useQuery({
    queryKey: ['tenant', tenantId, 'readings', ids.join(','), range],
    queryFn: () =>
      api.get<ReadingsResponse>(`/tenants/${encodeURIComponent(tenantId!)}/readings`, { query: { ids: ids.join(','), range } }),
    enabled: Boolean(tenantId) && ids.length > 0,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  const history = new Map<string, readonly HistoryPoint[]>();
  const stored = new Map((query.data?.series ?? []).map((s) => [s.indicatorId, s]));
  for (const id of ids) {
    const base: HistoryPoint[] = (stored.get(id)?.points ?? []).map((p) => [Date.parse(p.ts), p.value] as const);
    const lastStored = base.length ? base[base.length - 1]![0] : -Infinity;
    const tail = (live.get(id) ?? []).filter(([ts]) => ts > lastStored);
    history.set(id, tail.length ? [...base, ...tail] : base);
  }
  return { history, isLoading: query.isLoading };
}
